import { useState, useEffect } from 'react';
import { useAuthContext } from './AuthProvider';
import { applyReferralCode } from '../lib/api';
import './ReferralCodePrompt.css';

/**
 * Post-signup modal prompt for entering an invite code.
 *
 * Shown once after the user's first sign-in if no referral was auto-applied
 * via URL. Persists dismissal in localStorage.
 */
export default function ReferralCodePrompt() {
  const { user, loading } = useAuthContext();
  const [visible, setVisible] = useState(false);
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  useEffect(() => {
    if (loading || !user) return;

    // Only show for newly registered users (account created within last 5 minutes)
    const createdAt = new Date(user.created_at);
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    if (createdAt < fiveMinutesAgo) return;

    // Don't show if a referral was already auto-applied from URL
    if (localStorage.getItem('latted_referral_applied')) return;

    // Don't show if already dismissed
    if (localStorage.getItem('latted_referral_prompt_seen')) return;

    // Don't show if there's still a pending referral code (auto-apply in progress)
    if (localStorage.getItem('latted_referral_code')) return;

    // Small delay so it doesn't flash immediately on sign-in
    const timer = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(timer);
  }, [user, loading]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem('latted_referral_prompt_seen', 'true');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setMessage(null);

    try {
      const result = await applyReferralCode(trimmed);
      if (result.applied) {
        setMessage({ type: 'success', text: 'Invite code applied! You\'ll get $10 when you purchase $10+ in credits.' });
        localStorage.setItem('latted_referral_applied', 'true');
        setTimeout(handleDismiss, 3000);
      } else {
        setMessage({ type: 'error', text: 'Could not apply this code.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Invalid invite code.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!visible) return null;

  return (
    <div className="referral-prompt-backdrop" role="presentation" onClick={handleDismiss}>
      <div
        className="referral-prompt-card"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="referral-prompt-header">
          <h3>Have an invite code?</h3>
          <p>Enter it to earn <strong>$10 in free credits</strong></p>
        </div>

        <form className="referral-prompt-form" onSubmit={handleSubmit}>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.toUpperCase());
              if (message) setMessage(null);
            }}
            placeholder="e.g. LTTDAB12"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            disabled={submitting}
          />
          <button type="submit" disabled={submitting || !code.trim()}>
            {submitting ? 'Applying...' : 'Apply'}
          </button>
        </form>

        {message && (
          <div className={`referral-prompt-message referral-prompt-message--${message.type}`}>
            {message.text}
          </div>
        )}

        <button
          type="button"
          className="referral-prompt-skip"
          onClick={handleDismiss}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
